import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { RefreshCw, Trash2, Clock, CheckCircle, XCircle, Loader2, PlayCircle, AlertTriangle, RotateCcw, ChevronDown, ChevronUp, Download, FileArchive, File } from 'lucide-react';
import { toast } from 'sonner';

const TasksDashboard = () => {
  const { token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedTask, setExpandedTask] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [downloadingBatch, setDownloadingBatch] = useState(null); // Track individual batch downloads

  const fetchTasks = useCallback(async () => {
    try {
      const response = await api.get('/tasks', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Auto-refresh every 3 seconds if enabled
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchTasks, 3000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchTasks]);

  const cancelTask = async (taskId) => {
    try {
      await api.delete(`/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
    } catch (error) {
      console.error('Error cancelling task:', error);
    }
  };

  // Download a single batch
  const downloadBatch = async (task, batchNum) => {
    const downloadKey = `${task.task_id}-batch-${batchNum}`;
    setDownloadingBatch(downloadKey);
    try {
      const response = await api.get(`/events/${task.event_id}/badges/download-batch/${task.task_id}/${batchNum}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `badges_batch_${batchNum}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Batch ${batchNum} downloaded successfully!`);
    } catch (error) {
      console.error('Error downloading batch:', error);
      handleDownloadError(error);
    } finally {
      setDownloadingBatch(null);
    }
  };

  // Download all batches as ZIP
  const downloadAllAsZip = async (task) => {
    setDownloading(`${task.task_id}-zip`);
    try {
      const response = await api.get(`/events/${task.event_id}/badges/download-all-zip/${task.task_id}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `badges_all_${task.successful_badges || task.total}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('All badges downloaded as ZIP!');
    } catch (error) {
      console.error('Error downloading ZIP:', error);
      handleDownloadError(error);
    } finally {
      setDownloading(null);
    }
  };

  // Legacy single-file download (for backward compatibility)
  const downloadBadges = async (task) => {
    setDownloading(task.task_id);
    try {
      const response = await api.get(`/events/${task.event_id}/badges/download-prepared/${task.task_id}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `badges_${task.successful_badges || task.total}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Badges downloaded successfully!');
    } catch (error) {
      console.error('Error downloading badges:', error);
      handleDownloadError(error);
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadError = async (error) => {
    if (error.response) {
      const status = error.response.status;
      let message = 'Failed to download badges';
      
      if (error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          message = json.detail || message;
        } catch {
          // Ignore parse errors
        }
      } else if (error.response.data?.detail) {
        message = error.response.data.detail;
      }
      
      if (status === 410 || status === 404) {
        toast.error(message + '\n\nPlease start a new download from the Attendees page.', { duration: 8000 });
      } else {
        toast.error(message);
      }
    } else if (error.request) {
      toast.error('Network error. Please check your connection and try again.');
    } else {
      toast.error('Failed to download: ' + error.message);
    }
  };

  const retryFailedBadgeDownload = async (task) => {
    setRetrying(task.task_id);
    try {
      const response = await api.post(`/events/${task.event_id}/badges/retry-failed-download/${task.task_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Retrying ${response.data.total} failed badges...`);
      fetchTasks();
    } catch (error) {
      console.error('Error retrying badge download:', error);
      toast.error('Failed to retry: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRetrying(null);
    }
  };

  const retryFailedBadges = async (task) => {
    setRetrying(task.task_id);
    try {
      await api.post(`/events/${task.event_id}/badges/retry-failed`, {
        task_id: task.task_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
    } catch (error) {
      console.error('Error retrying badges:', error);
      toast.error('Failed to retry: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRetrying(null);
    }
  };

  const retryFailedWhatsApp = async (task) => {
    setRetrying(task.task_id);
    try {
      await api.post(`/events/${task.event_id}/badges/retry-failed-send`, {
        task_id: task.task_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
    } catch (error) {
      console.error('Error retrying WhatsApp send:', error);
      toast.error('Failed to retry: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRetrying(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'completed_with_failures': return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'completed_with_errors': return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'running': return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'pending': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'cancelled': return <XCircle className="h-4 w-4 text-gray-500" />;
      default: return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: 'bg-green-100 text-green-800',
      completed_with_failures: 'bg-yellow-100 text-yellow-800',
      completed_with_errors: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      running: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      completed: 'Completed',
      completed_with_failures: 'Partial Success',
      completed_with_errors: 'Completed with errors',
      failed: 'Failed',
      running: 'Running',
      pending: 'Pending',
      cancelled: 'Cancelled'
    };
    return (
      <Badge className={variants[status] || 'bg-gray-100 text-gray-800'}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getTaskTypeLabel = (type) => {
    const labels = {
      'generate_badge': 'Badge Generation',
      'generate_bulk_badges': 'Bulk Badge Generation',
      'bulk_badge_download': 'Bulk Badge Download',
      'retry_failed_badges': 'Retry Failed Badges',
      'retry_bulk_badges': 'Retry Badge Generation',
      'send_badge_email': 'Send Email',
      'send_bulk_notifications': 'Bulk Notifications',
      'bulk_send_whatsapp': 'Bulk WhatsApp Send',
      'bulk_send_email': 'Bulk Email Send',
      'retry_bulk_send_whatsapp': 'Retry WhatsApp Send',
      'import_attendees': 'Import Attendees'
    };
    return labels[type] || type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
  };

  const hasFailedAttendees = (task) => {
    return task.failed_attendees && task.failed_attendees.length > 0;
  };

  const hasBatchFiles = (task) => {
    return task.batch_files && task.batch_files.length > 0;
  };

  const canRetry = (task) => {
    return (task.status === 'completed_with_errors' || task.status === 'completed_with_failures' || task.status === 'failed') && hasFailedAttendees(task);
  };

  const isWhatsAppTask = (task) => {
    return task.task_type?.includes('whatsapp') || task.task_type?.includes('send');
  };

  const isBadgeDownloadTask = (task) => {
    return task.task_type === 'bulk_badge_download' || task.task_type === 'retry_failed_badges';
  };

  const canDownload = (task) => {
    return isBadgeDownloadTask(task) && 
           (task.status === 'completed' || task.status === 'completed_with_failures') && 
           (task.result_file_path || hasBatchFiles(task));
  };

  const runningTasks = tasks.filter(t => t.status === 'running' || t.status === 'pending');

  // Calculate if event is large (more than 5 batches = 500+ attendees)
  const isLargeEvent = (task) => {
    return task.batch_files && task.batch_files.length > 5;
  };

  // Render batch download section for a task
  const renderBatchDownloads = (task) => {
    if (!hasBatchFiles(task)) {
      // Legacy single-file download
      return (
        <Button
          variant="default"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            downloadBadges(task);
          }}
          disabled={downloading === task.task_id}
          className="bg-green-600 hover:bg-green-700"
        >
          {downloading === task.task_id ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Download className="h-4 w-4 mr-1" />
              Download {task.successful_badges || task.total}
            </>
          )}
        </Button>
      );
    }

    // New batch-based downloads
    const totalBatches = task.batch_files.length;
    const isLarge = isLargeEvent(task);
    
    return (
      <div className="flex flex-wrap gap-2 items-center">
        {/* For large events, recommend individual batches */}
        {isLarge ? (
          <>
            <span className="text-sm text-orange-600 font-medium">Large event - download individual batches recommended</span>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                downloadAllAsZip(task);
              }}
              disabled={downloading === `${task.task_id}-zip`}
              className="border-orange-300 text-orange-600"
              data-testid="download-all-zip-btn"
            >
              {downloading === `${task.task_id}-zip` ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <FileArchive className="h-4 w-4 mr-1" />
                  Try ZIP ({totalBatches} files)
                </>
              )}
            </Button>
          </>
        ) : (
          <>
            {/* Download All as ZIP button - primary action for small/medium events */}
            <Button
              variant="default"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                downloadAllAsZip(task);
              }}
              disabled={downloading === `${task.task_id}-zip`}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="download-all-zip-btn"
            >
              {downloading === `${task.task_id}-zip` ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <FileArchive className="h-4 w-4 mr-1" />
                  Download ZIP ({totalBatches} files)
                </>
              )}
            </Button>
            {/* Expandable batch list hint */}
            <span className="text-sm text-gray-500">or individual batches:</span>
          </>
        )}
      </div>
    );
  };

  // Render expanded batch details
  const renderBatchDetails = (task) => {
    if (!hasBatchFiles(task)) return null;

    return (
      <div className="border-t bg-blue-50 p-4">
        <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
          <File className="h-4 w-4" />
          Download Individual Batches ({task.batch_files.length} batches, {task.successful_badges} badges total)
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {task.batch_files.map((batch) => (
            <Button
              key={batch.batch_num}
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                downloadBatch(task, batch.batch_num);
              }}
              disabled={downloadingBatch === `${task.task_id}-batch-${batch.batch_num}`}
              className="flex items-center justify-between text-xs"
              data-testid={`download-batch-${batch.batch_num}-btn`}
            >
              {downloadingBatch === `${task.task_id}-batch-${batch.batch_num}` ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <span>Batch {batch.batch_num}</span>
                  <span className="text-gray-500 ml-1">({batch.badge_count})</span>
                </>
              )}
            </Button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Each batch contains up to 100 badges. Files expire after 24 hours.
        </p>
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="tasks-dashboard">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Background Tasks</h1>
            <p className="text-gray-500">Monitor badge generation, notifications, and imports</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={autoRefresh ? "default" : "outline"}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              data-testid="auto-refresh-toggle"
            >
              {autoRefresh ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-1" />}
              {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchTasks} data-testid="refresh-tasks">
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Running Tasks */}
        {runningTasks.length > 0 && (
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                Running Tasks ({runningTasks.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {runningTasks.map(task => (
                <div key={task.task_id} className="bg-white rounded-lg p-4 mb-2 shadow-sm">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="font-medium">{getTaskTypeLabel(task.task_type)}</span>
                      {getStatusBadge(task.status)}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => cancelTask(task.task_id)}
                      className="text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {task.total > 0 && (
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Progress</span>
                        <span>{task.progress || 0} / {task.total}</span>
                      </div>
                      <Progress value={((task.progress || 0) / task.total) * 100} className="h-2" />
                    </div>
                  )}
                  {task.progress_message && (
                    <div className="text-sm text-blue-600 mt-1">{task.progress_message}</div>
                  )}
                  {task.results && (
                    <div className="text-xs text-gray-600 mt-2">
                      {task.results.generated !== undefined && <span className="mr-3">Generated: {task.results.generated}</span>}
                      {task.results.failed !== undefined && task.results.failed > 0 && <span className="text-red-600">Failed: {task.results.failed}</span>}
                      {task.results.whatsapp_sent !== undefined && <span className="mr-3">Sent: {task.results.whatsapp_sent}</span>}
                      {task.results.whatsapp_failed !== undefined && task.results.whatsapp_failed > 0 && <span className="text-red-600">Failed: {task.results.whatsapp_failed}</span>}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-2">
                    Started: {formatDate(task.created_at)}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* All Tasks Table */}
        <Card>
          <CardHeader>
            <CardTitle>Task History</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No background tasks yet. Tasks will appear here when you generate badges in bulk or send notifications.
              </div>
            ) : (
              <div className="space-y-2">
                {tasks.map(task => (
                  <div key={task.task_id} className="border rounded-lg overflow-hidden">
                    {/* Task Row */}
                    <div 
                      className={`p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer ${hasFailedAttendees(task) || hasBatchFiles(task) ? 'bg-gray-50' : ''}`}
                      onClick={() => setExpandedTask(expandedTask === task.task_id ? null : task.task_id)}
                    >
                      <div className="flex items-center gap-4">
                        {getStatusIcon(task.status)}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{getTaskTypeLabel(task.task_type)}</span>
                            {getStatusBadge(task.status)}
                            {hasBatchFiles(task) && (
                              <Badge className="bg-blue-100 text-blue-800 text-xs">
                                {task.batch_files.length} batches
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(task.created_at)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Progress */}
                        {task.total > 0 && (
                          <div className="flex items-center gap-2">
                            <Progress value={((task.progress || 0) / task.total) * 100} className="w-20 h-2" />
                            <span className="text-sm text-gray-500 w-20">
                              {task.progress || 0}/{task.total}
                            </span>
                          </div>
                        )}
                        
                        {/* Results summary */}
                        <div className="text-sm w-48">
                          {/* Badge download task results */}
                          {isBadgeDownloadTask(task) && (task.status === 'completed' || task.status === 'completed_with_failures') && (
                            <>
                              <span className="text-green-600 mr-2">✓{task.successful_badges || 0}</span>
                              {task.failed_badges > 0 && (
                                <span className="text-red-600">✗{task.failed_badges}</span>
                              )}
                            </>
                          )}
                          {/* Other task results */}
                          {!isBadgeDownloadTask(task) && task.results && (
                            <>
                              {task.results.generated !== undefined && (
                                <span className="text-green-600 mr-2">✓{task.results.generated}</span>
                              )}
                              {task.results.whatsapp_sent !== undefined && (
                                <span className="text-green-600 mr-2">✓{task.results.whatsapp_sent}</span>
                              )}
                              {(task.results.failed > 0 || task.results.whatsapp_failed > 0) && (
                                <span className="text-red-600">✗{task.results.failed || task.results.whatsapp_failed}</span>
                              )}
                            </>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {/* Download Buttons for badge download tasks */}
                          {canDownload(task) && renderBatchDownloads(task)}
                          
                          {/* Retry Button for badge download with failures */}
                          {isBadgeDownloadTask(task) && task.status === 'completed_with_failures' && task.failed_badges > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                retryFailedBadgeDownload(task);
                              }}
                              disabled={retrying === task.task_id}
                              className="text-orange-600 border-orange-300"
                            >
                              {retrying === task.task_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <RotateCcw className="h-4 w-4 mr-1" />
                                  Retry {task.failed_badges}
                                </>
                              )}
                            </Button>
                          )}
                          
                          {/* Retry for other task types */}
                          {!isBadgeDownloadTask(task) && canRetry(task) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                isWhatsAppTask(task) ? retryFailedWhatsApp(task) : retryFailedBadges(task);
                              }}
                              disabled={retrying === task.task_id}
                              className="text-blue-600"
                            >
                              {retrying === task.task_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <RotateCcw className="h-4 w-4 mr-1" />
                                  Retry Failed
                                </>
                              )}
                            </Button>
                          )}
                          
                          {(task.status === 'running' || task.status === 'pending') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                cancelTask(task.task_id);
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                          {(hasFailedAttendees(task) || hasBatchFiles(task)) && (
                            expandedTask === task.task_id ? 
                              <ChevronUp className="h-5 w-5 text-gray-400" /> : 
                              <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Section - Batch Downloads */}
                    {expandedTask === task.task_id && hasBatchFiles(task) && !hasFailedAttendees(task) && renderBatchDetails(task)}
                    
                    {/* Expanded Section - Failed Attendees */}
                    {expandedTask === task.task_id && hasFailedAttendees(task) && (
                      <>
                        {/* Show batch downloads first if available */}
                        {hasBatchFiles(task) && renderBatchDetails(task)}
                        
                        {/* Failed attendees table */}
                        <div className="border-t bg-red-50 p-4">
                          <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4" />
                            Failed Attendees ({task.failed_attendees.length})
                          </h4>
                          <div className="max-h-60 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-red-700">
                                  <th className="pb-2">Ticket ID</th>
                                  <th className="pb-2">Name</th>
                                  {!isBadgeDownloadTask(task) && <th className="pb-2">Phone</th>}
                                  <th className="pb-2">Error / Reason</th>
                                  {isBadgeDownloadTask(task) && <th className="pb-2">Attempts</th>}
                                  {!isBadgeDownloadTask(task) && <th className="pb-2">Stage</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {task.failed_attendees.map((fa, idx) => (
                                  <tr key={idx} className="border-t border-red-200">
                                    <td className="py-2">{fa.ticket_id || '-'}</td>
                                    <td className="py-2">{fa.name || '-'}</td>
                                    {!isBadgeDownloadTask(task) && <td className="py-2">{fa.phone_number || '-'}</td>}
                                    <td className="py-2 text-red-600 max-w-xs truncate" title={fa.error || fa.reason}>
                                      {fa.error || fa.reason || '-'}
                                    </td>
                                    {isBadgeDownloadTask(task) && (
                                      <td className="py-2">
                                        <Badge variant="outline" className="text-xs">
                                          {fa.attempts || 1}
                                        </Badge>
                                      </td>
                                    )}
                                    {!isBadgeDownloadTask(task) && (
                                      <td className="py-2">
                                        <Badge variant="outline" className="text-xs">
                                          {fa.stage || 'unknown'}
                                        </Badge>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default TasksDashboard;
